import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["active", "aria-invalid", "aria-current", "aria-checked", "autoFocus", "block", "children", "disabled", "external", "iconProps", "loading", "onBlur", "onClick", "onFocus", "onKeyDown", "onKeyUp", "onMouseDown", "pressed", "responsive", "size", "title", "truncate", "truncateStringProps", "truncateTooltip", "use", "type"];
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { getSizeTokens, getUseTokens, isLinkUse, isFormUse, setAsActive, shouldButtonBeUnderlined, isTransparentUse, isLinkLikeUse, getControlMapping, isControlUse } from './Button.helpers';
import { renderChildren, renderExternalButtonIcon, renderTruncatedContents } from './Button.utils';
import styled, { useTheme } from 'foundations-theming-specialty/utils';
import { useDisplayOptimizedCssStyles, useResponsiveStyles, useSizeCssObject, useUseVariantCssObject } from './Button.hooks';
import { customizeStylesWithImportant } from '../../../utils/customizeStylesWithImportant';
import emptyFunction from 'react-utils/emptyFunction';
import { filterProps } from '../../../utils/filterProps';
import { mergeProps } from 'react-aria';
import { useColorSwatchContext } from '../ColorSwatch/ColorSwatchProvider';
import { useIconButtonContext } from '../IconButton/IconButtonProvider';
import { useLoadingButtonContext } from '../LoadingButton/LoadingButtonProvider';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { deepMerge } from '../../utils/deepMerge';
import { useAbstractDropdownContext } from '../AbstractDropdown/AbstractDropdownProvider';
import { useDropdownContext } from '../Dropdown/DropdownContext';
import { useFloatingButtonGroupContext } from '../FloatingButtonGroup/FloatingButtonGroupContext';
import { StyledContentWrapper } from '../Typeahead/TypeaheadResultsItem';
import { useFilterBarContext } from '../FilterBar/FilterBarContext';
import { CSS_SPECIFICITY } from '../../constants/CSSSpecificity';
import { useSelectContext } from '../Select/SelectProvider';
import { getSharedButtonContainerStyles } from './Button.helpers';
import { getFocusStyleObject } from '../../utils/focus/focus.style';
import { useControlContext } from '../Link/ControlContext';
import { TRANSITION_ALL_STYLE } from '../../../constants/transition-all';
import { jsx as _jsx } from "react/jsx-runtime";
export const StyledButton = styled.button.withConfig({
  displayName: "PrivateButton__StyledButton"
})(({
  $block,
  $responsiveStyles,
  $sizeCssStyles,
  $tokenCssStyles,
  $typography,
  $variantCssStyles,
  $isLinkUse,
  $displayOptimizedCssStyles,
  $styleOverrides,
  $isTransparentUse,
  $alwaysUnderline,
  $use,
  $isInDropdown,
  theme
}) => {
  return deepMerge(Object.assign({
    cursor: 'pointer',
    transition: TRANSITION_ALL_STYLE
  }, $variantCssStyles, $sizeCssStyles, theme.typography[$typography], $displayOptimizedCssStyles, $responsiveStyles, $block && {
    width: '100%'
  }, $isLinkUse && {
    fontWeight: theme.typography['body-125'].fontWeight,
    fontSize: 'inherit',
    lineHeight: 'inherit',
    // Override the whiteSpace: 'nowrap' style set by StyledContentWrapper
    // when link buttons are used inside dropdown/typeahead contexts, allowing text to wrap naturally
    [`& ${StyledContentWrapper}`]: {
      whiteSpace: 'normal !important'
    }
  }, $isTransparentUse && {
    fontWeight: theme.typography['body-125'].fontWeight
  }, $isInDropdown && {
    // allows buttons and links to have the same boldness within Dropdown
    '-webkit-font-smoothing': 'antialiased',
    '-moz-osx-font-smoothing': 'grayscale'
  }, getFocusStyleObject({
    theme,
    addBorderRadius: isLinkLikeUse($use),
    hasTransitionAll: true
  }), {
    // Apply underline logic for link variants and trellis theme
    textDecoration: $alwaysUnderline ? 'underline' : 'none'
  }), $styleOverrides, {
    // this guarantees that user defined styles will override button styles defined by other components
    [CSS_SPECIFICITY.FORCE_OVERRIDE]: Object.assign({}, $tokenCssStyles)
  });
});
export const UnstyledButton = styled.button.withConfig({
  displayName: "PrivateButton__UnstyledButton"
})(({
  $block,
  $responsiveStyles,
  $tokenCssStyles,
  $isLinkUse,
  $styleOverrides,
  $alwaysUnderline,
  $isInDropdown,
  theme
}) => {
  return deepMerge(Object.assign({
    background: 'none',
    border: 0,
    padding: 0,
    cursor: 'pointer'
  }, $responsiveStyles, $block && {
    width: '100%'
  }, $isLinkUse && {
    fontWeight: theme.typography['body-125'].fontWeight,
    fontSize: 'inherit',
    lineHeight: 'inherit'
  }, {
    // Apply underline logic for unstyled buttons when appropriate
    textDecoration: $alwaysUnderline ? 'underline' : 'none'
  }, $isInDropdown && {
    // allows buttons and links to have the same boldness within Dropdown
    '-webkit-font-smoothing': 'antialiased',
    '-moz-osx-font-smoothing': 'grayscale'
  }, getFocusStyleObject({
    theme,
    addBorderRadius: true
  })),
  // $tokenCssStyles: token props set by the user (includes space and border props).
  // $styleOverrides: button style overrides from other components.
  // $tokenCssStyles should take precedence over $styleOverrides because it is set by the user.
  $styleOverrides, $tokenCssStyles);
});
/*
 * We needed to explicitly type this function because Styled Components was having an issue with the union type of the $typography prop.
 * See https://hubspot.slack.com/archives/C4ABAB8CR/p1722362981866609
 */
export const StyledButtonTextContainer = styled.span.withConfig({
  displayName: "PrivateButton__StyledButtonTextContainer"
})(({
  $use,
  theme
}) => {
  return Object.assign({}, getSharedButtonContainerStyles(theme, $use), {
    justifyContent: isLinkUse($use) || isFormUse($use) || $use === 'transparent' ? 'flex-start' : 'center',
    boxSizing: 'border-box',
    fontStyle: 'unset',
    textTransform: 'unset',
    backgroundColor: 'unset',
    margin: theme.space['0'],
    padding: theme.space['0'],
    color: 'inherit',
    // The color is set on the button element via the variant tokens, so we can just inherit it here
    textAlign: isLinkUse($use) || isFormUse($use) ? 'left' : 'center'
  });
});
export const PrivateButton = /*#__PURE__*/forwardRef((_ref, ref) => {
  var _controlContext$isCon, _selectContext$button, _iconButtonContext$bu, _loadingButtonContext, _abstractDropdownCont, _dropdownContext$butt, _colorSwatchContext$b;
  let {
      active = false,
      'aria-invalid': ariaInvalid,
      'aria-current': ariaCurrent,
      'aria-checked': ariaChecked,
      autoFocus = false,
      block,
      children,
      disabled = false,
      external,
      iconProps,
      loading = false,
      onBlur = emptyFunction,
      onClick = emptyFunction,
      onFocus = emptyFunction,
      onKeyDown = emptyFunction,
      onKeyUp = emptyFunction,
      onMouseDown = emptyFunction,
      pressed,
      responsive,
      size,
      title,
      truncate,
      truncateStringProps,
      truncateTooltip,
      use: rawUse,
      type
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const controlContext = useControlContext();
  const isControl = (_controlContext$isCon = controlContext === null || controlContext === void 0 ? void 0 : controlContext.isControl) !== null && _controlContext$isCon !== void 0 ? _controlContext$isCon : isControlUse(rawUse);
  const use = isControl ? getControlMapping(rawUse) : rawUse;
  const useTokens = getUseTokens(use);
  const {
    floatingGroup
  } = useFloatingButtonGroupContext();
  const buttonRef = useRef(null);
  const handleClick = e => {
    // should not prevent default if button is a menu item it prevents links in tooltips around the disabled option from being clicked
    if (disabled || loading) {
      if ((rest === null || rest === void 0 ? void 0 : rest.role) !== 'menuitem') {
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }
    onClick(e);
  };
  const handleFocus = e => {
    onFocus(e);
  };
  const handleBlur = e => {
    onBlur(e);
  };
  const buttonProps = Object.assign({
    onBlur: handleBlur,
    onClick: handleClick,
    onFocus: handleFocus,
    onKeyDown,
    onKeyUp,
    onMouseDown
  }, rest);
  useImperativeHandle(ref, () => buttonRef.current, [buttonRef]);
  if (active) {
    setAsActive(useTokens);
  }
  const useVariantCssStyles = useUseVariantCssObject(isLinkUse(use), useTokens);
  const sizeTokens = getSizeTokens(size, use, floatingGroup);
  const sizeCssStyles = useSizeCssObject(sizeTokens);
  const {
    domProps,
    tokenProps,
    callbackProps,
    restProps
  } = filterProps(buttonProps);

  // restProps contains ButtonHTMLAttributes, which need to be passed to the button element
  const mergedProps = mergeProps(domProps, callbackProps, restProps);
  const iconButtonContext = useIconButtonContext();
  const loadingButtonContext = useLoadingButtonContext();
  const abstractDropdownContext = useAbstractDropdownContext();
  const dropdownContext = useDropdownContext();
  const filterBarContext = useFilterBarContext();
  const colorSwatchContext = useColorSwatchContext();
  const selectContext = useSelectContext();
  const theme = useTheme();
  const selectButtonStyleOverrides = (_selectContext$button = selectContext === null || selectContext === void 0 ? void 0 : selectContext.buttonStyleOverrides) !== null && _selectContext$button !== void 0 ? _selectContext$button : {};
  const {
    tokenProps: selectTokenProps
  } = filterProps(selectButtonStyleOverrides);
  const customizeStyles = customizeStylesWithImportant(/^margin/);
  const tokenCssStyles = useMapTokenPropsToCSS(tokenProps, {
    customizeStyles
  });
  const responsiveStyles = useResponsiveStyles({
    responsive,
    use,
    isFilterBar: filterBarContext === null || filterBarContext === void 0 ? void 0 : filterBarContext.isFilterBar
  });
  const displayOptimizedCssStyles = useDisplayOptimizedCssStyles(use);
  const sharedStyleOverrides = deepMerge((_iconButtonContext$bu = iconButtonContext === null || iconButtonContext === void 0 ? void 0 : iconButtonContext.buttonStyleOverrides) !== null && _iconButtonContext$bu !== void 0 ? _iconButtonContext$bu : {}, (_loadingButtonContext = loadingButtonContext === null || loadingButtonContext === void 0 ? void 0 : loadingButtonContext.getButtonStyleOverrides(theme)) !== null && _loadingButtonContext !== void 0 ? _loadingButtonContext : {});
  const newSharedStyleOverrides = deepMerge(sharedStyleOverrides, (_abstractDropdownCont = abstractDropdownContext === null || abstractDropdownContext === void 0 ? void 0 : abstractDropdownContext.buttonStyleOverrides) !== null && _abstractDropdownCont !== void 0 ? _abstractDropdownCont : {});
  const dropdownSharedStyleOverrides = deepMerge(newSharedStyleOverrides, (_dropdownContext$butt = dropdownContext === null || dropdownContext === void 0 ? void 0 : dropdownContext.buttonStyleOverrides) !== null && _dropdownContext$butt !== void 0 ? _dropdownContext$butt : {});
  const privateButtonStyleOverrides = deepMerge(dropdownSharedStyleOverrides, (_colorSwatchContext$b = colorSwatchContext === null || colorSwatchContext === void 0 ? void 0 : colorSwatchContext.buttonStyleOverrides) !== null && _colorSwatchContext$b !== void 0 ? _colorSwatchContext$b : {});
  const selectButtonOverrides = deepMerge(dropdownSharedStyleOverrides, selectTokenProps);
  const renderedChildren = renderChildren(children);
  const renderedExternalButtonIcon = renderExternalButtonIcon(external, iconProps);
  const renderedContents = truncate ? renderTruncatedContents({
    truncateStringProps,
    truncateTooltip,
    renderedChildren,
    renderedExternalButtonIcon,
    use
  }) : renderedChildren;
  if (use === 'unstyled') {
    return /*#__PURE__*/_jsx(UnstyledButton, Object.assign({}, mergedProps, {
      "aria-disabled": disabled || loading,
      "aria-invalid": ariaInvalid,
      "aria-current": ariaCurrent,
      "aria-checked": ariaChecked,
      "aria-pressed": pressed,
      autoFocus: autoFocus,
      "data-fnd-button": "t",
      ref: buttonRef,
      title: title,
      "data-button-use": use,
      type: type,
      $block: block,
      $tokenCssStyles: tokenCssStyles,
      $responsiveStyles: responsiveStyles,
      $isLinkUse: isLinkUse(use),
      $styleOverrides: privateButtonStyleOverrides,
      $alwaysUnderline: shouldButtonBeUnderlined(use, theme),
      $isInDropdown: dropdownContext === null || dropdownContext === void 0 ? void 0 : dropdownContext.isDropdown,
      children: /*#__PURE__*/_jsx(StyledButtonTextContainer, {
        $use: use,
        children: renderedContents
      })
    }));
  }
  return /*#__PURE__*/_jsx(StyledButton, Object.assign({}, mergedProps, {
    "aria-disabled": disabled || loading,
    "aria-invalid": ariaInvalid,
    "aria-current": ariaCurrent,
    "aria-checked": ariaChecked,
    "aria-pressed": pressed,
    autoFocus: autoFocus,
    "data-fnd-button": "t",
    ref: buttonRef,
    title: title,
    "data-button-use": use,
    type: type,
    $block: block,
    $variantCssStyles: useVariantCssStyles,
    $sizeCssStyles: sizeCssStyles,
    $tokenCssStyles: tokenCssStyles,
    $typography: sizeTokens.typography,
    $responsiveStyles: responsiveStyles,
    $isLinkUse: isLinkUse(use),
    $displayOptimizedCssStyles: displayOptimizedCssStyles,
    $styleOverrides: selectButtonOverrides,
    $isTransparentUse: isTransparentUse(use),
    $alwaysUnderline: shouldButtonBeUnderlined(use, theme),
    $use: use,
    $isInDropdown: dropdownContext === null || dropdownContext === void 0 ? void 0 : dropdownContext.isDropdown,
    children: /*#__PURE__*/_jsx(StyledButtonTextContainer, {
      $use: use,
      children: renderedContents
    })
  }));
});
PrivateButton.displayName = 'PrivateButton';